import {
  createCreationClipSuggestion as createCreationClipSuggestionAction,
  createCreationClipSuggestionFeedback,
  getCreationClipSuggestion,
  getCreationClipSuggestions,
  reprocessCreationClipSuggestion,
} from 'redux/middleware/api/creation-service/actions';
import { actions as podcastService } from 'redux/middleware/api/podcast-service';
import { ThunkAction } from 'redux/types';
import { IEmbedConfig } from 'types';
import { ClipSuggestionDislikeReason } from 'types/common';
import { reduxPoll } from 'utils/redux-poll';
import {
  creationSuggestionSelector,
  groupClipSuggestionSelector,
} from '../entities';
import { Type } from './action-types';
import {
  CLIP_FEEDBACK_REASON_TO_TYPE,
  CREATION_CLIP_SUGGESTION_POLL_ID,
  GROUP_CLIP_SUGGESTION_POLL_ID,
} from './constants';
import { subscriptionItemIdSelector } from './selectors';

export const loadGroupClipSuggestions = (
  episodeId: number,
  subscriptionItemId: number,
  groupRequestId?: number,
): ThunkAction<Promise<void>> => async dispatch => {
  dispatch({
    type: Type.LOAD_GROUP_CLIP_SUGGESTIONS_REQUEST,
  });

  try {
    await dispatch(
      podcastService.getGroupClipSuggestions(
        episodeId,
        subscriptionItemId,
        groupRequestId,
      ),
    );

    dispatch({
      type: Type.LOAD_GROUP_CLIP_SUGGESTIONS_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: Type.LOAD_GROUP_CLIP_SUGGESTIONS_FAILURE,
      payload: { isBlocked: error.status === 403 },
    });
  }
};

export const loadSubscriptionItemInformation = (
  subscriptionItemId: number,
): ThunkAction<Promise<void>> => async dispatch => {
  dispatch({ type: Type.LOAD_SUBSCRIPTION_ITEM_INFORMATION_REQUEST });

  try {
    await dispatch(
      podcastService.getSubscriptionItemInformation(subscriptionItemId),
    );

    dispatch({
      type: Type.LOAD_SUBSCRIPTION_ITEM_INFORMATION_SUCCESS,
    });
  } catch (error) {
    dispatch({ type: Type.LOAD_SUBSCRIPTION_ITEM_INFORMATION_FAILURE });
  }
};

export const dislikeGroupSuggestedClip = (
  reason: ClipSuggestionDislikeReason,
  suggestionId: number,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  const subscriptionItemId = subscriptionItemIdSelector(getState());

  dispatch({
    type: Type.GROUP_CLIP_SUGGESTION_DISLIKE,
    payload: { suggestionId },
  });

  // This was discussed this with BE. The suggestionId is not meant to be sent
  // for the audio clips
  await dispatch(
    podcastService.createEpisodeClipSuggestionFeedback({
      subscriptionItemId,
      autoCreateEpisodeId: suggestionId,
      feedbackType: CLIP_FEEDBACK_REASON_TO_TYPE[reason],
    }),
  );
};

export type PollForGroupClipSuggestionOptions = {
  startMillis?: number;
  endMillis?: number;
  isReprocessing?: boolean;
  isPreviewRequested?: boolean;
};

export const pollForGroupClipSuggestion = (
  autoCreateEpisodeId: number,
  opts?: PollForGroupClipSuggestionOptions,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  const { startMillis, endMillis, isReprocessing, isPreviewRequested } =
    opts ?? {};

  const newClipSuggestion = {
    autoCreateEpisodeId,
    startMillis,
    endMillis,
    isVideoOutdated: false,
    status: isReprocessing ? 'exportingVideo' : 'queued',
    text: '',
    videoUrl: null,
    widgetId: null,
    isPreviewRequested,
  };

  if (
    (startMillis !== undefined && endMillis !== undefined) ||
    isReprocessing
  ) {
    dispatch({
      type: Type.GROUP_CLIP_SUGGESTION_POLLING_REQUEST,
      response: {
        entities: {
          groupClipSuggestion: {
            [autoCreateEpisodeId]: newClipSuggestion,
          },
        },
      },
    });
  }

  await reduxPoll(
    dispatch,
    async () => {
      await dispatch(
        podcastService.getGroupClipSuggestion(autoCreateEpisodeId),
      );

      const response = groupClipSuggestionSelector(
        autoCreateEpisodeId,
        getState(),
      );

      return response;
    },
    {
      id: `${GROUP_CLIP_SUGGESTION_POLL_ID}_${autoCreateEpisodeId}`,
      shouldContinue: (_, value) =>
        value?.status !== 'completed' && value?.status !== 'failed',
      intervalMillis: 2500,
      maxAttempts: Infinity,
    },
  );
};

export const createGroupClipSuggestion = (
  episodeId: number,
  subItemId: number,
  groupRequestId: number,
  replaceAutoCreateEpisodeId: number,
  newTrimStartMillis: number,
  newTrimEndMillis: number,
): ThunkAction<Promise<number | void>> => async dispatch => {
  const { response } = await dispatch(
    podcastService.createGroupClipSuggestion(
      episodeId,
      subItemId,
      groupRequestId,
      replaceAutoCreateEpisodeId,
      newTrimStartMillis,
      newTrimEndMillis,
    ),
  );

  // updates the list of group clip suggestions in redux to include the new suggestion
  await dispatch(
    podcastService.getGroupClipSuggestions(
      episodeId,
      subItemId,
      groupRequestId,
    ),
  );

  dispatch(
    pollForGroupClipSuggestion(response.autoCreateEpisodeId, {
      startMillis: newTrimStartMillis,
      endMillis: newTrimEndMillis,
    }),
  );

  return response.autoCreateEpisodeId;
};

export const reprocessGroupClipSuggestion = (
  autoCreateEpisodeId: number,
  subItemId: number,
  groupRequestId: number,
  newTrimStartMillis: number,
  newTrimEndMillis: number,
  configJson?: IEmbedConfig,
  isPreviewRequest?: boolean,
): ThunkAction<Promise<void>> => async dispatch => {
  await dispatch(
    podcastService.reprocessAutoCreateEpisode({
      autoCreateEpisodeId,
      groupRequestId,
      subItemId,
      newTrimStartMillis,
      newTrimEndMillis,
      configJson,
      isPreviewRequest,
    }),
  );

  await dispatch(
    pollForGroupClipSuggestion(autoCreateEpisodeId, {
      startMillis: newTrimStartMillis,
      endMillis: newTrimEndMillis,
      isReprocessing: true,
      isPreviewRequested: isPreviewRequest,
    }),
  );
};

export const loadCreationClipSuggestions = (
  creationRequestId: number,
): ThunkAction<Promise<void>> => async dispatch => {
  dispatch({
    type: Type.LOAD_CREATION_CLIP_SUGGESTIONS_REQUEST,
  });

  try {
    await dispatch(getCreationClipSuggestions(creationRequestId));

    dispatch({
      type: Type.LOAD_CREATION_CLIP_SUGGESTIONS_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: Type.LOAD_CREATION_CLIP_SUGGESTIONS_FAILURE,
      payload: { isBlocked: error.status === 403 },
    });
  }
};

export const dislikeCreationSuggestedClip = (
  reason: ClipSuggestionDislikeReason,
  creationRequestId?: number,
  suggestionId?: number,
): ThunkAction<Promise<void>> => async dispatch => {
  dispatch({
    type: Type.CREATION_CLIP_SUGGESTION_DISLIKE,
    payload: { suggestionId },
  });

  await dispatch(
    createCreationClipSuggestionFeedback(
      CLIP_FEEDBACK_REASON_TO_TYPE[reason],
      creationRequestId,
      suggestionId,
    ),
  );
};

export type PollForCreationClipSuggestionOptions = {
  isPreviewRequested?: boolean;
  isReprocessing?: boolean;
  endMillis?: number;
  startMillis?: number;
};

export const pollForCreationClipSuggestion = (
  creationRequestId: number,
  suggestionId: number,
  opts?: PollForCreationClipSuggestionOptions,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  const { isPreviewRequested, isReprocessing, endMillis, startMillis } =
    opts ?? {};

  const newSuggestion = {
    endMillis,
    isVideoOutdated: false,
    startMillis,
    status: isReprocessing ? 'exportingVideo' : 'queued',
    suggestionId,
    text: '',
    videoUrl: null,
    widgetId: null,
    isPreviewRequested,
  };

  if (
    (startMillis !== undefined && endMillis !== undefined) ||
    isReprocessing
  ) {
    dispatch({
      type: Type.CREATION_CLIP_SUGGESTION_POLLING_REQUEST,
      response: {
        entities: {
          creationClipSuggestion: {
            [suggestionId]: newSuggestion,
          },
        },
      },
    });
  }

  await reduxPoll(
    dispatch,
    async () => {
      await dispatch(
        getCreationClipSuggestion(creationRequestId, suggestionId),
      );

      return creationSuggestionSelector(suggestionId, getState());
    },
    {
      id: `${CREATION_CLIP_SUGGESTION_POLL_ID}_${suggestionId}`,
      shouldContinue: (_, value) =>
        value?.status !== 'completed' && value?.status !== 'failed',
      intervalMillis: 2500,
      maxAttempts: Infinity,
    },
  );
};

export const createCreationClipSuggestion = (
  creationRequestId: number,
  replaceSuggestionId: number,
  newTrimStartMillis: number,
  newTrimEndMillis: number,
): ThunkAction<Promise<number | void>> => async dispatch => {
  const { response } = await dispatch(
    createCreationClipSuggestionAction(
      creationRequestId,
      replaceSuggestionId,
      newTrimStartMillis,
      newTrimEndMillis,
    ),
  );

  // updates the list of creation clip suggestions in redux to include the new suggestio
  await dispatch(getCreationClipSuggestions(creationRequestId));

  dispatch(
    pollForCreationClipSuggestion(creationRequestId, response.suggestionId, {
      startMillis: newTrimStartMillis,
      endMillis: newTrimEndMillis,
    }),
  );

  return response.suggestionId;
};

export const reprocessCreationSuggestion = (
  creationRequestId: number,
  suggestionId: number,
  newTrimStartMillis: number,
  newTrimEndMillis: number,
  configJson?: IEmbedConfig,
  isPreviewRequest?: boolean,
): ThunkAction<Promise<void>> => async dispatch => {
  await dispatch(
    reprocessCreationClipSuggestion({
      creationRequestId,
      suggestionId,
      newTrimStartMillis,
      newTrimEndMillis,
      configJson,
      isPreviewRequest,
    }),
  );

  await dispatch(
    pollForCreationClipSuggestion(creationRequestId, suggestionId, {
      startMillis: newTrimStartMillis,
      endMillis: newTrimEndMillis,
      isReprocessing: true,
      isPreviewRequested: isPreviewRequest,
    }),
  );

  return undefined;
};

export const reset = () => ({
  type: Type.RESET,
});
